import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Bot, User } from "lucide-react";
import { RORK_AFFILIATE_LINK, LOVABLE_AFFILIATE_LINK } from "@/lib/constants";

interface Message {
  id: string;
  role: "user" | "assistant";
  content: string;
}

const getAIResponse = (userMessage: string): string => {
  const lowerMsg = userMessage.toLowerCase();
  
  if (lowerMsg.includes("mobile") || lowerMsg.includes("app") || lowerMsg.includes("ios") || lowerMsg.includes("android")) {
    return `For mobile apps, **Rork** is the better choice! It's designed for mobile-first development and generates code structured for iOS/Android.\n\n👉 [Try Rork](${RORK_AFFILIATE_LINK})`;
  }
  
  if (lowerMsg.includes("web") || lowerMsg.includes("website") || lowerMsg.includes("landing") || lowerMsg.includes("dashboard")) {
    return `For web apps and websites, **Lovable** is your best bet! It excels at landing pages, dashboards, and web prototypes with its chat-based building approach.\n\n👉 [Try Lovable](${LOVABLE_AFFILIATE_LINK})`;
  }
  
  if (lowerMsg.includes("beginner") || lowerMsg.includes("easy") || lowerMsg.includes("simple") || lowerMsg.includes("start")) {
    return `If you're a beginner, I'd recommend **Lovable**! It has a lower learning curve with its chat-based interface, making it easier to get quick wins.\n\n👉 [Try Lovable](${LOVABLE_AFFILIATE_LINK})`;
  }
  
  if (lowerMsg.includes("difference") || lowerMsg.includes("compare") || lowerMsg.includes("vs") || lowerMsg.includes("versus")) {
    return `Here's the key difference:\n\n**Rork** → Mobile-first apps (iOS/Android)\n**Lovable** → Web apps & websites\n\nBoth generate working code, but they're optimized for different platforms. What are you building?`;
  }
  
  if (lowerMsg.includes("price") || lowerMsg.includes("cost") || lowerMsg.includes("free")) {
    return `Pricing can change, so I'd recommend checking the official sites for the latest info:\n\n👉 [Rork](${RORK_AFFILIATE_LINK})\n👉 [Lovable](${LOVABLE_AFFILIATE_LINK})`;
  }

  if (lowerMsg.includes("both") || lowerMsg.includes("together")) {
    return `Yes, you can use both! A common workflow:\n\n1. Prototype with Lovable for speed\n2. Use Rork for mobile-specific features\n3. Iterate based on feedback\n\nStart with the platform that matters most for your v1.`;
  }

  return `Great question! To give you the best recommendation, could you tell me more about:\n\n1. **Platform**: Mobile app or web app?\n2. **Experience**: Are you technical or non-technical?\n3. **Timeline**: How quickly do you need results?\n\nThis will help me point you to the right tool!`;
};

const AIChatbot = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: "welcome",
      role: "assistant",
      content: "Hi! I'm your AI assistant. Tell me what you're building and I'll recommend the best tool for you.",
    },
  ]);
  const [input, setInput] = useState("");
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const handleSend = async () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      role: "user",
      content: input.trim(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setInput("");
    setIsTyping(true);

    await new Promise((resolve) => setTimeout(resolve, 800 + Math.random() * 700));

    const aiResponse: Message = {
      id: (Date.now() + 1).toString(),
      role: "assistant",
      content: getAIResponse(userMessage.content),
    };

    setMessages((prev) => [...prev, aiResponse]);
    setIsTyping(false);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const renderContent = (content: string) => {
    const linkRegex = /\[([^\]]+)\]\(([^)]+)\)/g;
    const parts = [];
    let lastIndex = 0;
    let match;

    while ((match = linkRegex.exec(content)) !== null) {
      if (match.index > lastIndex) {
        parts.push(content.slice(lastIndex, match.index));
      }
      parts.push(
        <a
          key={match.index}
          href={match[2]}
          target="_blank"
          rel="noopener noreferrer"
          className="font-medium text-primary underline hover:text-primary/80"
        >
          {match[1]}
        </a>
      );
      lastIndex = match.index + match[0].length;
    }

    if (lastIndex < content.length) {
      parts.push(content.slice(lastIndex));
    }

    return parts.map((part, i) => {
      if (typeof part === "string") {
        return part.split(/(\*\*[^*]+\*\*)/).map((segment, j) => {
          if (segment.startsWith("**") && segment.endsWith("**")) {
            return <strong key={`${i}-${j}`}>{segment.slice(2, -2)}</strong>;
          }
          return segment;
        });
      }
      return part;
    });
  };

  return (
    <>
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-primary text-primary-foreground shadow-lg transition-all hover:scale-105 hover:shadow-xl ${
          isOpen ? "scale-0 opacity-0" : "scale-100 opacity-100"
        }`}
        aria-label="Open chat"
      >
        <MessageCircle size={24} />
      </button>

      <div
        className={`fixed bottom-6 right-6 z-50 flex h-[500px] w-[380px] max-w-[calc(100vw-3rem)] flex-col overflow-hidden rounded-2xl border border-border/50 bg-card shadow-2xl transition-all ${
          isOpen
            ? "scale-100 opacity-100"
            : "pointer-events-none scale-95 opacity-0"
        }`}
      >
        <div className="flex items-center justify-between border-b border-border/50 bg-secondary/30 px-4 py-3">
          <div className="flex items-center gap-2">
            <div className="flex h-8 w-8 items-center justify-center rounded-full bg-primary/10">
              <Bot size={18} className="text-primary" />
            </div>
            <div>
              <h3 className="text-sm font-semibold text-foreground">AI Advisor</h3>
              <p className="text-xs text-muted-foreground">ToolMatch</p>
            </div>
          </div>
          <button
            onClick={() => setIsOpen(false)}
            className="rounded-lg p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
            aria-label="Close chat"
          >
            <X size={18} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto p-4">
          <div className="space-y-4">
            {messages.map((message) => (
              <div
                key={message.id}
                className={`flex gap-2 ${message.role === "user" ? "flex-row-reverse" : ""}`}
              >
                <div
                  className={`flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${
                    message.role === "user"
                      ? "bg-accent/10"
                      : "bg-primary/10"
                  }`}
                >
                  {message.role === "user" ? (
                    <User size={14} className="text-accent" />
                  ) : (
                    <Bot size={14} className="text-primary" />
                  )}
                </div>
                <div
                  className={`max-w-[80%] rounded-2xl px-4 py-2.5 text-sm ${
                    message.role === "user"
                      ? "bg-accent text-accent-foreground"
                      : "bg-secondary text-foreground"
                  }`}
                >
                  <div className="whitespace-pre-wrap">{renderContent(message.content)}</div>
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="flex gap-2">
                <div className="flex h-7 w-7 shrink-0 items-center justify-center rounded-full bg-primary/10">
                  <Bot size={14} className="text-primary" />
                </div>
                <div className="rounded-2xl bg-secondary px-4 py-2.5">
                  <div className="flex gap-1">
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: "0ms" }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: "150ms" }} />
                    <span className="h-2 w-2 animate-bounce rounded-full bg-muted-foreground" style={{ animationDelay: "300ms" }} />
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
        </div>

        <div className="border-t border-border/50 p-4">
          <div className="flex gap-2">
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={handleKeyPress}
              placeholder="Ask me anything..."
              className="flex-1 rounded-xl border border-border bg-secondary/50 px-4 py-2.5 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-1 focus:ring-primary"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim() || isTyping}
              className="flex h-10 w-10 items-center justify-center rounded-xl bg-primary text-primary-foreground transition-colors hover:bg-primary/90 disabled:opacity-50"
              aria-label="Send message"
            >
              <Send size={16} />
            </button>
          </div>
        </div>
      </div>
    </>
  );
};

export default AIChatbot;
